<template>
  <div
    v-if="indicator"
    class="relative z-2001 flex shrink-0 items-center justify-center"
    :style="{
      height: barHeight,
      backgroundColor: indicator.barBgColor,
    }">
    <span
      class="flex items-center rounded px-1.5 py-0.5 text-[9px] leading-none font-semibold tracking-[0.08em] uppercase"
      :style="{
        backgroundColor: indicator.badgeBgColor,
        color: indicator.badgeTextColor,
      }">
      {{ indicator.barLabel }}
    </span>
  </div>
</template>

<script lang="ts">
/**
 * Full-width 18px bar naming the environment this build is actually running
 * against — `DEVELOPMENT` on local and dev, `STAGE` on stage, nothing at all
 * on prod. A solid color carries the same "not production" signal at a
 * glance, for anyone who is not reading the words.
 *
 * Sits at the very top of the app, on every route including login, so dev,
 * stage and prod stop looking identical at a glance.
 *
 * Rendered as the first flex child of App.vue's `h-dvh` column rather than as
 * a fixed overlay, so the bar takes its 18px off the top and the routed view
 * shrinks to fit. Nothing is covered and no height math is needed.
 *
 * It does lift itself above everything else with a z-index, though. A modal's
 * backdrop (z-1000) covers the whole viewport and dims the bar along with the
 * rest of the app, which is exactly when you least want to lose track of which
 * environment you are in. 2001 clears every layer either frontend uses today:
 * the backdrop, the modal itself (1001), the global menu (1010) and the action
 * search overlay (2000). Nothing should ever paint over an 18px strip at the
 * top edge, so being topmost costs nothing.
 *
 * Not to be confused with `DevBadge`, which marks developer-only *UI* and says
 * nothing about which environment you are in. Like that badge this one takes no
 * props: the bar must look the same everywhere, in both frontends.
 *
 * The label is an environment name, not prose — it is intentionally not
 * translated.
 */
import {
  ENV_BAR_HEIGHT_PX,
  getEnvIndicator,
} from '@/globals/typescript/utils/envIndicator'

export default {
  name: 'EnvBar',
  computed: {
    // ENV is populated by initEnvironment() during bootstrap, long before any
    // component mounts, so this never has to react to a later change.
    indicator() {
      return getEnvIndicator()
    },

    // Straight from the constant rather than through --env-bar-height: the bar
    // must have its real height even if applyEnvBarHeight() never ran, so that
    // failure shows up as an overlap rather than as an invisible bar.
    barHeight(): string {
      return `${ENV_BAR_HEIGHT_PX}px`
    },
  },
}
</script>
